import PropTypes from "prop-types";
import { Card, CardContent, Typography, Box } from "@mui/material";
import { fCurrency } from "../../../../utils/formatNumber";
import React, { useMemo } from "react";
import DOMPurify from "dompurify";
import { useTheme } from "@mui/material/styles";
import { useLocales } from "../../../../locales";

// Only allow what the "simple" editor can produce (plus a few safe extras).
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    "p", "br", "span", "strong", "b", "em", "i", "u", "s", "strike",
    "h1", "h2", "h3", "h4", "h5", "h6",
    "ul", "ol", "li", "a", "blockquote",
  ],
  ALLOWED_ATTR: ["href", "target", "rel", "class", "data-list"],
  ALLOW_DATA_ATTR: false,
};

// Make all links open safely in a new tab.
// (Registered once at module level so it isn't added on every render.)
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A") {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer nofollow");
  }
});

const sanitize = (html) => DOMPurify.sanitize(html || "", SANITIZE_CONFIG);

ProductCard.propTypes = {
  name: PropTypes.string,
  description: PropTypes.string,
  quantityString: PropTypes.string,
  price: PropTypes.number,
  currency: PropTypes.string,
};

export default function ProductCard({ name, description, quantityString, price, currency, children }) {
  const theme = useTheme();
  const { translate } = useLocales();

  const safeDescription = useMemo(() => sanitize(description), [description]);
  // Treat "<p><br></p>" and similar as empty
  const hasDescription = safeDescription.replace(/<[^>]*>/g, "").trim().length > 0;

  return (
    <Card
      raised
      sx={{
        p: 2,
        mt: 2,
        backgroundColor: theme.palette.background.paper,
        borderRadius: "8px",
        display: "flex",
        justifyContent: "space-between",
        minHeight: 100,
      }}
    >
      <div style={{ display: "flex", flexDirection: "column" }}>
        <Typography variant="h6">{name}</Typography>

        {hasDescription && (
          <Box
            mt={1}
            sx={{
              typography: "body1",
              wordBreak: "break-word",
              "& > *:first-of-type": { mt: 0 },
              "& > *:last-child": { mb: 0 },
              "& p": { m: 0 },
              "& h1, & h2, & h3, & h4, & h5, & h6": { my: 0.5 },
              "& h1": { typography: "h4" },
              "& h2": { typography: "h5" },
              "& h3": { typography: "h6" },
              "& h4": { typography: "subtitle1" },
              "& h5": { typography: "subtitle2" },
              "& h6": { typography: "subtitle2" },
              "& ul, & ol": { my: 0.5, pl: 3 },
              "& ul": { listStyleType: "disc" },
              "& ol": { listStyleType: "decimal" },
              // Quill 2 renders bullets as <ol><li data-list="bullet">
              "& li[data-list='bullet']": { listStyleType: "disc" },
              "& a": { color: "primary.main", textDecoration: "underline" },
              "& blockquote": {
                m: 0,
                pl: 2,
                borderLeft: `4px solid ${theme.palette.divider}`,
              },
              // Quill alignment / direction classes
              "& .ql-align-center": { textAlign: "center" },
              "& .ql-align-right": { textAlign: "right" },
              "& .ql-align-justify": { textAlign: "justify" },
              "& .ql-direction-rtl": { direction: "rtl", textAlign: "right" },
            }}
            dangerouslySetInnerHTML={{ __html: safeDescription }}
          />
        )}

        {quantityString && (
          <Typography mt={1} variant="caption" color={"#666666"}>
            {quantityString}
          </Typography>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column" }}>
        <Typography
          variant="subtitle1"
          fontFamily="Visby"
          align="right"
          component={"div"}
          sx={{ pr: 0.5 }}
        >
          {price ? `${fCurrency(price)} ${currency}` : translate("eventManager.free")}
        </Typography>
        <div style={{ flexGrow: 1 }}></div>
        {children}
        <div style={{ flexGrow: 1 }}></div>
      </div>
    </Card>
  );
}